import axios, { AxiosError, AxiosResponse } from "axios";
import { call, put, takeEvery, takeLatest } from "@redux-saga/core/effects";

import { RootState } from "data-handler/rootReducer";
import {
  listObjectsToListIds,
  listToObject,
} from "data-handler/ducks/paginationTools";
import { errorToast } from "./toast";

export const NEEDS_REVIEW_LIST_REQUEST =
  "schoolconnect/needsreview/NEEDS_REVIEW_LIST_REQUEST";
export const NEEDS_REVIEW_LIST_SUCCESS =
  "schoolconnect/needsreview/NEEDS_REVIEW_LIST_SUCCESS";
export const NEEDS_REVIEW_LIST_FAILURE =
  "schoolconnect/needsreview/NEEDS_REVIEW_LIST_FAILURE";

export const NEEDS_REVIEW_RESOLVE_REQUEST =
  "schoolconnect/needsreview/NEEDS_REVIEW_RESOLVE_REQUEST";
export const NEEDS_REVIEW_RESOLVE_SUCCESS =
  "schoolconnect/needsreview/NEEDS_REVIEW_RESOLVE_SUCCESS";
export const NEEDS_REVIEW_RESOLVE_FAILURE =
  "schoolconnect/needsreview/NEEDS_REVIEW_RESOLVE_FAILURE";

export const NEEDS_REVIEW_COUNT_REQUEST =
  "schoolconnect/needsreview/NEEDS_REVIEW_COUNT_REQUEST";
export const NEEDS_REVIEW_COUNT_SUCCESS =
  "schoolconnect/needsreview/NEEDS_REVIEW_COUNT_SUCCESS";
export const NEEDS_REVIEW_COUNT_FAILURE =
  "schoolconnect/needsreview/NEEDS_REVIEW_COUNT_FAILURE";

export const NEEDS_REVIEW_IMPACT_REQUEST =
  "schoolconnect/needsreview/NEEDS_REVIEW_IMPACT_REQUEST";
export const NEEDS_REVIEW_IMPACT_SUCCESS =
  "schoolconnect/needsreview/NEEDS_REVIEW_IMPACT_SUCCESS";
export const NEEDS_REVIEW_IMPACT_FAILURE =
  "schoolconnect/needsreview/NEEDS_REVIEW_IMPACT_FAILURE";

export const NEEDS_REVIEW_APPLY_REQUEST =
  "schoolconnect/needsreview/NEEDS_REVIEW_APPLY_REQUEST";
export const NEEDS_REVIEW_APPLY_SUCCESS =
  "schoolconnect/needsreview/NEEDS_REVIEW_APPLY_SUCCESS";
export const NEEDS_REVIEW_APPLY_FAILURE =
  "schoolconnect/needsreview/NEEDS_REVIEW_APPLY_FAILURE";

// discard_fragment and keep_fragment delete a store event and move its legacy
// link; acknowledge only stamps the row
export type NeedsReviewApplyAction =
  | "discard_fragment"
  | "keep_fragment"
  | "acknowledge";

const omitKey = <T>(source: Record<string, T>, key: number | string) => {
  const { [String(key)]: _dropped, ...rest } = source;
  return rest;
};

// only deliveries and purchases are FIFO sources, so only they can have
// movements hanging off them. everything else skips the impact fetch entirely
const SOURCE_EVENT_TYPES = ["delivery", "purchase", "purchase_detail"];

export const isSourceMovement = (eventType?: string): boolean =>
  !!eventType && SOURCE_EVENT_TYPES.includes(eventType);

export type NeedsReviewRule = "subset" | "shell" | "disjoint" | "identical";

// the short tokens the backend's event_type filter accepts. "pur" deliberately
// covers both purchase and purchase_detail server-side
export type MovementType = "cons" | "del" | "inc" | "pur";

export const MOVEMENT_TYPES: Array<{ value: MovementType; label: string }> = [
  { value: "cons", label: "Consumption" },
  { value: "del", label: "Delivery" },
  { value: "inc", label: "Incident" },
  { value: "pur", label: "Purchase" },
];

export type NeedsReviewEventLine = {
  stock_id: number;
  stock_event_id: number;
  commodity: string;
  quantity: number;
  batch_no: string | null;
  linked_to_duplicate?: boolean;
};

export type NeedsReviewEvent = {
  id: number;
  event_type: string;
  category: string;
  occurred_at: string;
  meal_provided?: boolean;
  has_daily_report?: boolean;
  lines: NeedsReviewEventLine[];
};

export type NeedsReviewItem = {
  id: number;
  store_id: number;
  school_name: string;
  country_id: number;
  country_name: string | null;
  rule: NeedsReviewRule;
  reason: string;
  excess_event_id: number;
  keeper_event_id: number;
  occurred_at: string;
  event_type: string;
  resolved: boolean;
  resolved_at: string | null;
  date_created: string;
  // only populated for no-meal pairs, which are the only rows the apply
  // endpoint will act on
  classification?: Record<string, unknown> | null;
  // older backends may not send these yet, so both stay optional
  excess_event?: NeedsReviewEvent;
  keeper_event?: NeedsReviewEvent | null;
};

export type ImpactDependent = {
  event_id: number;
  event_type: string;
  occurred_at: string;
  stock_id: number;
  stock_event_id: number;
  commodity: string | null;
  quantity_drawn_from_source: number;
  can_refifo: boolean;
  shortfall: number;
  requires_human_judgment: boolean;
  warning: string;
};

export type ImpactBalance = {
  stock_id: number;
  commodity: string | null;
  balance_now: number;
  balance_after: number;
  min_rolling_now: number;
  min_rolling_after: number;
  goes_negative: boolean;
  temporarily_negative: boolean;
  already_negative: boolean;
  introduces_negative: boolean;
  worsens_negative: boolean;
};

export type NeedsReviewImpact = {
  has_dependents: boolean;
  blocking: boolean;
  needs_human_judgment?: boolean;
  dependents: ImpactDependent[];
  balance_impact: ImpactBalance[];
};

type NeedsReviewData = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Record<number, NeedsReviewItem>;
  ids: number[];
  // the countries actually represented in the queue, for the filter dropdown
  countries: Array<{ id: number; name: string }>;
};

type State = {
  fetching: boolean;
  resolveFetching: boolean;
  error: AxiosError | null;
  errorResponse: any;
  resolveError: AxiosError | null;
  resolveErrorResponse: any;
  data: NeedsReviewData;
  // per-stock open-item counts for the tab strip badge, keyed by stock id
  countByStockId: Record<string, number>;
  countFetchingStockIds: Record<string, boolean>;
  // deletion impact per needs-review item, keyed by item id
  impactById: Record<string, NeedsReviewImpact>;
  impactFetchingIds: Record<string, boolean>;
  applyFetchingIds: Record<string, boolean>;
};

type NeedsReviewListRequestAction = {
  type: typeof NEEDS_REVIEW_LIST_REQUEST;
  pageSize: number;
  pageIndex: number;
  filters: Array<{ id: string; value: string }>;
};

type NeedsReviewListSuccessAction = {
  type: typeof NEEDS_REVIEW_LIST_SUCCESS;
  data: NeedsReviewData;
};

type NeedsReviewListFailureAction = {
  type: typeof NEEDS_REVIEW_LIST_FAILURE;
  error: AxiosError;
};

type NeedsReviewResolveRequestAction = {
  type: typeof NEEDS_REVIEW_RESOLVE_REQUEST;
  ids: number[];
  resolved: boolean;
  // carry the current filter state so we can refetch after resolve
  refetchAction: NeedsReviewListRequestAction;
};

type NeedsReviewApplyRequestAction = {
  type: typeof NEEDS_REVIEW_APPLY_REQUEST;
  itemId: number;
  action: NeedsReviewApplyAction;
  refetchAction: NeedsReviewListRequestAction;
};

type NeedsReviewResolveSuccessAction = {
  type: typeof NEEDS_REVIEW_RESOLVE_SUCCESS;
};

type NeedsReviewResolveFailureAction = {
  type: typeof NEEDS_REVIEW_RESOLVE_FAILURE;
  error: AxiosError;
};

type NeedsReviewCountRequestAction = {
  type: typeof NEEDS_REVIEW_COUNT_REQUEST;
  stockId: string;
};

type NeedsReviewCountSuccessAction = {
  type: typeof NEEDS_REVIEW_COUNT_SUCCESS;
  stockId: string;
  count: number;
};

type NeedsReviewCountFailureAction = {
  type: typeof NEEDS_REVIEW_COUNT_FAILURE;
  stockId: string;
  error: AxiosError;
};

type NeedsReviewImpactRequestAction = {
  type: typeof NEEDS_REVIEW_IMPACT_REQUEST;
  itemId: number;
};

type NeedsReviewImpactSuccessAction = {
  type: typeof NEEDS_REVIEW_IMPACT_SUCCESS;
  itemId: number;
  impact: NeedsReviewImpact;
};

type NeedsReviewImpactFailureAction = {
  type: typeof NEEDS_REVIEW_IMPACT_FAILURE;
  itemId: number;
  error: AxiosError;
};

type Action =
  | NeedsReviewListRequestAction
  | NeedsReviewListSuccessAction
  | NeedsReviewListFailureAction
  | NeedsReviewResolveRequestAction
  | NeedsReviewResolveSuccessAction
  | NeedsReviewResolveFailureAction
  | NeedsReviewCountRequestAction
  | NeedsReviewCountSuccessAction
  | NeedsReviewCountFailureAction
  | NeedsReviewImpactRequestAction
  | NeedsReviewImpactSuccessAction
  | NeedsReviewImpactFailureAction;

const EMPTY_DATA: NeedsReviewData = {
  count: 0,
  next: null,
  previous: null,
  results: {},
  ids: [],
  countries: [],
};

const initialState: State = {
  fetching: false,
  resolveFetching: false,
  error: null,
  errorResponse: null,
  resolveError: null,
  resolveErrorResponse: null,
  data: EMPTY_DATA,
  countByStockId: {},
  countFetchingStockIds: {},
  impactById: {},
  impactFetchingIds: {},
  applyFetchingIds: {},
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case NEEDS_REVIEW_LIST_REQUEST:
      return { ...state, fetching: true, error: null, errorResponse: null };
    case NEEDS_REVIEW_LIST_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        errorResponse: null,
        data: action.data,
      };
    case NEEDS_REVIEW_LIST_FAILURE:
      return {
        ...state,
        fetching: false,
        error: action.error,
        errorResponse: action.error.response,
      };
    case NEEDS_REVIEW_RESOLVE_REQUEST:
      return {
        ...state,
        resolveFetching: true,
        resolveError: null,
        resolveErrorResponse: null,
      };
    case NEEDS_REVIEW_RESOLVE_SUCCESS:
      return {
        ...state,
        resolveFetching: false,
        resolveError: null,
        resolveErrorResponse: null,
      };
    case NEEDS_REVIEW_RESOLVE_FAILURE:
      return {
        ...state,
        resolveFetching: false,
        resolveError: action.error,
        resolveErrorResponse: action.error.response,
      };
    case NEEDS_REVIEW_COUNT_REQUEST:
      return {
        ...state,
        countFetchingStockIds: {
          ...state.countFetchingStockIds,
          [action.stockId]: true,
        },
      };
    case NEEDS_REVIEW_COUNT_SUCCESS:
      return {
        ...state,
        countFetchingStockIds: {
          ...state.countFetchingStockIds,
          [action.stockId]: false,
        },
        countByStockId: {
          ...state.countByStockId,
          [action.stockId]: action.count,
        },
      };
    case NEEDS_REVIEW_COUNT_FAILURE:
      return {
        ...state,
        countFetchingStockIds: {
          ...state.countFetchingStockIds,
          [action.stockId]: false,
        },
      };
    case NEEDS_REVIEW_IMPACT_REQUEST:
      return {
        ...state,
        impactFetchingIds: {
          ...state.impactFetchingIds,
          [action.itemId]: true,
        },
      };
    case NEEDS_REVIEW_IMPACT_SUCCESS:
      return {
        ...state,
        impactFetchingIds: {
          ...state.impactFetchingIds,
          [action.itemId]: false,
        },
        impactById: { ...state.impactById, [action.itemId]: action.impact },
      };
    case NEEDS_REVIEW_IMPACT_FAILURE:
      return {
        ...state,
        impactFetchingIds: {
          ...state.impactFetchingIds,
          [action.itemId]: false,
        },
      };
    case NEEDS_REVIEW_APPLY_REQUEST:
      return {
        ...state,
        applyFetchingIds: { ...state.applyFetchingIds, [action.itemId]: true },
      };
    case NEEDS_REVIEW_APPLY_SUCCESS:
      return {
        ...state,
        applyFetchingIds: { ...state.applyFetchingIds, [action.itemId]: false },
        // the applied row's impact is stale the moment the events change
        impactById: omitKey(state.impactById, action.itemId),
      };
    case NEEDS_REVIEW_APPLY_FAILURE:
      return {
        ...state,
        applyFetchingIds: { ...state.applyFetchingIds, [action.itemId]: false },
      };
    default:
      return state;
  }
};

export default reducer;

function fetchNeedsReviewList(action: NeedsReviewListRequestAction) {
  const { pageSize, pageIndex, filters } = action;
  const offset = (pageIndex - 1) * pageSize;

  const qs = filters
    .filter(({ value }) => value && value !== "")
    .map(({ id, value }) => `&${id}=${value}`)
    .join("");

  return axios({
    method: "GET",
    url:
      `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/needs-review/` +
      `?limit=${pageSize}&offset=${offset}${qs}`,
  });
}

function postNeedsReviewResolve(ids: number[], resolved: boolean) {
  return axios({
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/needs-review/resolve/`,
    data: { ids, resolved },
  });
}

function fetchNeedsReviewCount(stockId: string) {
  return axios({
    method: "GET",
    url:
      `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/needs-review/` +
      `?stock_id=${stockId}&resolved=false&limit=1`,
  });
}

function postNeedsReviewApply(itemId: number, action: NeedsReviewApplyAction) {
  return axios({
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/needs-review/${itemId}/apply/`,
    data: { action },
  });
}

function fetchNeedsReviewImpact(itemId: number) {
  return axios({
    method: "GET",
    url: `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/needs-review/${itemId}/impact/`,
  });
}

export function* workerNeedsReviewListSaga(
  action: NeedsReviewListRequestAction
) {
  try {
    const response: AxiosResponse = yield call(fetchNeedsReviewList, action);
    const results = listToObject(response.data.results, "id");
    const ids = listObjectsToListIds(response.data.results, "id");
    const data: NeedsReviewData = {
      ...response.data,
      results,
      ids,
      countries: response.data.countries ?? [],
    };
    yield put({ type: NEEDS_REVIEW_LIST_SUCCESS, data });
  } catch (error) {
    yield put({ type: NEEDS_REVIEW_LIST_FAILURE, error });
  }
}

export function* workerNeedsReviewResolveSaga(
  action: NeedsReviewResolveRequestAction
) {
  try {
    yield call(postNeedsReviewResolve, action.ids, action.resolved);
    yield put({ type: NEEDS_REVIEW_RESOLVE_SUCCESS });
    yield put(action.refetchAction);
  } catch (error) {
    yield put({ type: NEEDS_REVIEW_RESOLVE_FAILURE, error });
    yield put(errorToast());
  }
}

export function* workerNeedsReviewCountSaga(
  action: NeedsReviewCountRequestAction
) {
  const { stockId } = action;
  try {
    const response: AxiosResponse = yield call(fetchNeedsReviewCount, stockId);
    yield put({
      type: NEEDS_REVIEW_COUNT_SUCCESS,
      stockId,
      count: response.data?.count ?? 0,
    });
  } catch (error) {
    yield put({ type: NEEDS_REVIEW_COUNT_FAILURE, stockId, error });
  }
}

export function* workerNeedsReviewImpactSaga(
  action: NeedsReviewImpactRequestAction
) {
  const { itemId } = action;
  try {
    const response: AxiosResponse = yield call(fetchNeedsReviewImpact, itemId);
    yield put({
      type: NEEDS_REVIEW_IMPACT_SUCCESS,
      itemId,
      impact: response.data,
    });
  } catch (error) {
    yield put({ type: NEEDS_REVIEW_IMPACT_FAILURE, itemId, error });
  }
}

export function* needsReviewListSagaWatcher() {
  yield takeLatest(NEEDS_REVIEW_LIST_REQUEST, workerNeedsReviewListSaga);
}

export function* needsReviewResolveSagaWatcher() {
  yield takeLatest(NEEDS_REVIEW_RESOLVE_REQUEST, workerNeedsReviewResolveSaga);
}

// takeEvery, not takeLatest — counts are keyed per stock id, so a fetch for
// one stock shouldn't cancel an in-flight fetch for another
export function* needsReviewCountSagaWatcher() {
  yield takeEvery(NEEDS_REVIEW_COUNT_REQUEST, workerNeedsReviewCountSaga);
}

// takeEvery for the same reason: several rows on a page fetch their own impact
export function* workerNeedsReviewApplySaga(
  action: NeedsReviewApplyRequestAction
) {
  const { itemId } = action;
  try {
    yield call(postNeedsReviewApply, itemId, action.action);
    yield put({ type: NEEDS_REVIEW_APPLY_SUCCESS, itemId });
    yield put(action.refetchAction);
  } catch (error) {
    yield put({ type: NEEDS_REVIEW_APPLY_FAILURE, itemId, error });
    yield put(errorToast());
  }
}

export function* needsReviewImpactSagaWatcher() {
  yield takeEvery(NEEDS_REVIEW_IMPACT_REQUEST, workerNeedsReviewImpactSaga);
}

export function* needsReviewApplySagaWatcher() {
  // takeLatest would cancel a running apply, and these delete rows — a request
  // already in flight must be allowed to finish
  yield takeEvery(NEEDS_REVIEW_APPLY_REQUEST, workerNeedsReviewApplySaga);
}

export const requestNeedsReviewList = (
  pageSize: number,
  pageIndex: number,
  filters: Array<{ id: string; value: string }>
): NeedsReviewListRequestAction => ({
  type: NEEDS_REVIEW_LIST_REQUEST,
  pageSize,
  pageIndex,
  filters,
});

export const requestNeedsReviewResolve = (
  ids: number[],
  resolved: boolean,
  refetchAction: NeedsReviewListRequestAction
): NeedsReviewResolveRequestAction => ({
  type: NEEDS_REVIEW_RESOLVE_REQUEST,
  ids,
  resolved,
  refetchAction,
});

export const getNeedsReviewData = (state: RootState): NeedsReviewData =>
  state.needsReview.data;

export const getIsNeedsReviewFetching = (state: RootState): boolean =>
  state.needsReview.fetching;

export const getIsNeedsReviewResolveFetching = (state: RootState): boolean =>
  state.needsReview.resolveFetching;

export const getNeedsReviewError = (state: RootState): AxiosError | null =>
  state.needsReview.error;

export const requestNeedsReviewCount = (
  stockId: string
): NeedsReviewCountRequestAction => ({
  type: NEEDS_REVIEW_COUNT_REQUEST,
  stockId,
});

// undefined means "not fetched yet", distinct from a real count of 0
export const getNeedsReviewCountForStock = (
  state: RootState,
  stockId: string
): number | undefined => state.needsReview.countByStockId[stockId];

export const getIsNeedsReviewCountFetching = (
  state: RootState,
  stockId: string
): boolean => state.needsReview.countFetchingStockIds[stockId] === true;

export const requestNeedsReviewImpact = (
  itemId: number
): NeedsReviewImpactRequestAction => ({
  type: NEEDS_REVIEW_IMPACT_REQUEST,
  itemId,
});

// undefined means "not fetched yet", distinct from a report with no dependents
export const getNeedsReviewImpact = (
  state: RootState,
  itemId: number
): NeedsReviewImpact | undefined => state.needsReview.impactById[itemId];

export const getIsNeedsReviewImpactFetching = (
  state: RootState,
  itemId: number
): boolean => state.needsReview.impactFetchingIds[itemId] === true;

export const requestNeedsReviewApply = (
  itemId: number,
  action: NeedsReviewApplyAction,
  refetchAction: NeedsReviewListRequestAction
): NeedsReviewApplyRequestAction => ({
  type: NEEDS_REVIEW_APPLY_REQUEST,
  itemId,
  action,
  refetchAction,
});

export const getIsNeedsReviewApplyFetching = (
  state: RootState,
  itemId: number
): boolean => state.needsReview.applyFetchingIds[itemId] === true;
